///////////////////////////////////////////////////////////////////////////////
// Market home (/uk): one page for a market, selling everything we have there.
// Top-ups, gift cards, game vouchers and eSIM data as rows of brand tiles.
//
// The config (markets.js) says what to feature; names, logos and prices come
// from the live catalogue (the same endpoints the country pages use). Prices
// are quoted in the market's currency. Checkout still charges USD or THB.
///////////////////////////////////////////////////////////////////////////////
import React, { useEffect, useMemo, useState } from 'react';
import { Header, track, carrierColors, useLogoManifest, flagEmoji } from './Chrome';
import { PayBox, SiteFooterV2 } from './Footers';
import { VoiceButton, VOICE_HINTS } from './VoiceSearch';
import { resolveHero, heroStyle } from './hero';
import { slugifyCarrierName, carrierBrandName } from './carrierSlug';
import { SLUG_BY_ISO } from '../../lib/countrySlugs';

const TYPE_LABEL = { topup: 'Top-up', gift: 'Gift card', game: 'Game', esim: 'eSIM' };
const LIGHT_PLATE = '#f3f3f5';

// Search hints per type, so "apple gift card" and "sim data" find the right tiles.
const ALIASES = {
  apple: 'itunes app store', 'google play': 'android play store', amazon: 'shopping',
  uber: 'taxi ride', 'just eat': 'food takeaway delivery', 'playstation store': 'playstation ps4 ps5 sony',
  xbox: 'microsoft', ee: 'everything everywhere', three: '3', lyca: 'lycamobile',
  'at-t': 'att at&t', 't-mobile': 'tmobile', 'verizon-wireless': 'verizon', 'metro-pcs': 'metro metropcs',
  'boost-mobile': 'boost', 'cricket-mobile': 'cricket', 'simple-mobile': 'simple', netflix: 'streaming movies tv',
  doordash: 'door dash food delivery', 'razer gold': 'razer',
};
const STOP = new Set('a an the for to my me i we want need buy get send please of in on and some up top topup pounds pound gbp quid card cards voucher'.split(' '));

function tokens(q) {
  const raw = String(q || '').toLowerCase().replace(/[£$€]/g, ' ').split(/[^a-z0-9&+]+/).filter(Boolean);
  const t = raw.filter((w) => !STOP.has(w) && !/^\d+$/.test(w));
  return t.length ? t : raw.filter((w) => !/^\d+$/.test(w));
}
const wordStart = (hay, tok) => new RegExp(`(^|[^a-z0-9])${tok.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`).test(hay);

function money(symbol, n) {
  const v = Number(n);
  if (!isFinite(v)) return null;
  return `${symbol}${Number.isInteger(v) ? v : v.toFixed(2)}`;
}

// ---- catalogue -> tiles ------------------------------------------------------

function useMarketData(market) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let dead = false;
    const get = (u) => fetch(u).then((r) => (r.ok ? r.json() : null)).catch(() => null);
    Promise.all([
      get(`/api/gifts/browse/${market.iso}`),
      get(`/api/airtime/browse/${market.iso}`),
      get('/api/esims/locations'),
      get('/api/esims/catalog?search=Europe'),
      market.currency === 'USD' ? Promise.resolve(null) : get(`/api/market/fx?cur=${market.currency}`),
    ]).then(([gifts, airtime, locations, europe, fx]) => {
      if (!dead) setData({ gifts, airtime, locations, europe, fx });
    });
    return () => { dead = true; };
  }, [market.iso, market.currency]);
  return data;
}

function giftTile(market, data, name, kind) {
  const g = data && data.gifts;
  if (!g || g.status !== 'ok') return null;
  const p = (g.providers || []).find((x) => String(x.Name).toLowerCase() === name.toLowerCase());
  if (!p) return null;
  const faces = (g.products || [])
    .filter((x) => x.ProviderCode === p.ProviderCode && x.Minimum && x.Minimum.ReceiveCurrencyIso === market.currency)
    .map((x) => x.Minimum.ReceiveValue);
  const label = name === 'Apple' ? 'Apple Gift Card' : name;
  return {
    id: `${kind}:${name}`, kind, label, logo: p.LogoUrl, market: market.key,
    plate: market.plates[name.toLowerCase()] || LIGHT_PLATE,
    from: faces.length ? money(market.symbol, Math.min(...faces)) : null,
    href: `/gifts/${market.slug}?brand=${encodeURIComponent(p.brandKey || name)}`,
    q: `${label} ${ALIASES[name.toLowerCase()] || ''} ${TYPE_LABEL[kind]}`.toLowerCase(),
  };
}

function networkTiles(market, data) {
  const a = data && data.airtime;
  if (!a || a.status !== 'ok') return [];
  const ops = [...(a.operators || []), ...(a.bundle_operators || []), ...(a.pin_operators || [])];
  // A brand can be listed several times (fixed amounts and an open range, bundles):
  // one tile per brand slug, with the lowest amount across all of its listings.
  const bySlug = new Map();
  for (const op of ops) {
    const slug = slugifyCarrierName(op.name);
    if (!slug || (market.hide || []).includes(slug)) continue;
    const faces = (op.amounts || []).map((x) => (x.local_amount > 0 ? x.local_amount : x.amount_usd)).filter((v) => v > 0);
    // An open range's minimum in USD already carries a few cents on top of the face
    // value ($5.10 for a $5 card), so show the whole-dollar face.
    if (op.local_min > 0) faces.push(op.local_min); else if (op.min_usd > 0) faces.push(Math.floor(op.min_usd + 0.001));
    const cur = bySlug.get(slug);
    if (!cur) bySlug.set(slug, { slug, op, low: faces.length ? Math.min(...faces) : null, logo: op.logo_url || null });
    else {
      if (faces.length) cur.low = cur.low == null ? Math.min(...faces) : Math.min(cur.low, ...faces);
      if (!cur.logo && op.logo_url) { cur.logo = op.logo_url; cur.op = op; }
    }
  }
  const out = [];
  for (const { slug, op, low, logo } of bySlug.values()) {
    const label = (market.names && market.names[slug]) || carrierBrandName(op.name);
    const brand = carrierColors(op.name, market.iso);
    out.push({
      id: `topup:${slug}`, slug, kind: 'topup', label, logo, market: market.key,
      plate: market.plates[slug] || (brand && brand.main) || LIGHT_PLATE,
      from: low ? money(market.symbol, low) : null,
      href: `/topup/${market.slug}/${slug}`,
      q: `${label} ${ALIASES[slug] || ''} top up topup mobile sim credit`.toLowerCase(),
    });
  }
  return out;
}

function esimTiles(market, data) {
  if (!data) return [];
  // USD markets need no conversion; others use the display rate from /api/market/fx.
  const perUsd = market.currency === 'USD' ? 1 : (data.fx && data.fx.status === 'ok' ? Number(data.fx.usd_per) : null);
  const gbp = (usd) => (perUsd && isFinite(usd) ? money(market.symbol, Math.round((usd / perUsd) * 100) / 100) : null);
  const out = [];
  const eur = data.europe && data.europe.products ? data.europe.products.filter((p) => p.is_regional) : [];
  if (eur.length) {
    out.push({
      id: 'esim:EU', kind: 'esim', label: 'Europe', sub: '40+ countries', flag: '🇪🇺', market: market.key,
      from: gbp(Math.min(...eur.map((p) => p.price))), href: '/dataplans',
      q: 'europe esim data sim just data travel',
    });
  }
  const locs = (data.locations && data.locations.locations) || [];
  for (const code of market.esim) {
    const l = locs.find((x) => x.code === code);
    if (!l) continue;
    out.push({
      id: `esim:${code}`, kind: 'esim', label: l.name, flag: flagEmoji(code), market: market.key,
      from: gbp(l.from_price), href: SLUG_BY_ISO[code] ? `/esim/${SLUG_BY_ISO[code]}` : '/dataplans',
      q: `${l.name} esim data sim just data travel`.toLowerCase(),
    });
  }
  return out;
}

// ---- pieces -------------------------------------------------------------------

function Tile({ t, badge }) {
  const onClick = () => track('market_tile', { market: t.market, id: t.id });
  if (t.kind === 'esim') {
    return (
      <a className="mk-tile" href={t.href} data-q={t.q} onClick={onClick}>
        <span className="mk-plate esim">
          <span className="mk-flag" aria-hidden="true">{t.flag}</span>
          <span className="mk-jd"><i>Just</i><b>Data</b></span>
        </span>
        <span className="mk-name">{t.label}<span className="mk-sub">{[badge && 'eSIM', t.sub, t.from ? `from ${t.from}` : 'see plans'].filter(Boolean).join(' · ')}</span></span>
      </a>
    );
  }
  return (
    <a className="mk-tile" href={t.href} data-q={t.q} onClick={onClick}>
      <span className="mk-plate" style={{ background: t.plate }}>
        {t.logo ? <img src={t.logo} alt="" decoding="async" /> : <span className="mk-initials">{t.label}</span>}
      </span>
      <span className="mk-name">{t.label}{(badge || t.from) && <span className="mk-sub">{[badge && TYPE_LABEL[t.kind], t.from && `from ${t.from}`].filter(Boolean).join(' · ')}</span>}</span>
    </a>
  );
}

function Row({ id, title, more, children, hidden }) {
  if (hidden) return null;
  return (
    <section className="mk-row" id={id}>
      <div className="mk-row-head"><h2>{title}</h2>{more && <a href={more.href}>{more.label}</a>}</div>
      {children}
    </section>
  );
}

const ICON = {
  all: <path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z" />,
  topup: <><rect x="7" y="2.5" width="10" height="19" rx="2.2" /><path d="M11 18.5h2" /></>,
  gift: <><rect x="3.5" y="8" width="17" height="12" rx="1.5" /><path d="M12 8v12M3.5 12.5h17M12 8c-1-3.5-5-3.5-5-1.2C7 8.4 9 8 12 8zm0 0c1-3.5 5-3.5 5-1.2C17 8.4 15 8 12 8z" /></>,
  game: <><rect x="2.5" y="7" width="19" height="10.5" rx="5" /><path d="M7.5 10.5v3.5M5.8 12.2h3.4" /><circle cx="16.5" cy="11" r=".8" /><circle cx="18.5" cy="13" r=".8" /></>,
  esim: <><path d="M7 3h7l4 4v14H7z" /><rect x="10" y="12" width="5" height="5" rx="1" /></>,
};
const CHIPS = [['all', 'Show all'], ['topup', 'Mobile top-up'], ['gift', 'Gift cards'], ['game', 'Game vouchers'], ['esim', 'eSIM data']];

// ---- page ---------------------------------------------------------------------

export default function MarketHome({ market, me, currency, onCurrency }) {
  const logosReady = useLogoManifest();   // network plate colours arrive with the manifest
  const data = useMarketData(market);
  const [heroUrl, setHeroUrl] = useState(null);
  const [kind, setKind] = useState('all');
  const [filter, setFilter] = useState('');
  const [searching, setSearching] = useState(false);
  const [voiceState, setVoiceState] = useState('idle');

  useEffect(() => { resolveHero(market.name, setHeroUrl, market.iso); }, [market.name, market.iso]);
  useEffect(() => { track('market_home', { market: market.key }); }, [market.key]);

  const tiles = useMemo(() => {
    const gifts = market.gifts.map((n) => giftTile(market, data, n, 'gift')).filter(Boolean);
    const games = market.games.map((n) => giftTile(market, data, n, 'game')).filter(Boolean);
    const nets = networkTiles(market, data);
    const major = market.networks.map((s) => nets.find((n) => n.slug === s)).filter(Boolean);
    const minor = nets.filter((n) => !market.networks.includes(n.slug));
    const esim = esimTiles(market, data);
    const byId = Object.fromEntries([...gifts, ...nets, ...esim].map((t) => [t.id, t]));
    const popular = market.popular.map((p) => {
      const [k, key] = p.split(':');
      return byId[k === 'topup' ? `topup:${key}` : (k === 'esim' ? `esim:${key}` : `gift:${key}`)];
    }).filter(Boolean);
    return { gifts, games, major, minor, esim, popular };
  }, [market, data, logosReady]);

  const tk = useMemo(() => tokens(filter), [filter]);
  const searchingLocal = tk.length > 0;
  const match = (t) => !searchingLocal || tk.every((w) => wordStart(t.q, w));
  const rows = {
    topup: [...tiles.major, ...tiles.minor].filter(match),
    gift: tiles.gifts.filter(match),
    game: tiles.games.filter(match),
    esim: tiles.esim.filter(match),
  };
  const anyMatch = rows.topup.length + rows.gift.length + rows.game.length + rows.esim.length > 0;
  const show = (k) => (searchingLocal ? rows[k].length > 0 : (kind === 'all' || kind === k));

  // Enter / voice: send it through the plain-language parser (checkout or the
  // right page when it understands the request); otherwise keep the tile filter.
  async function runSearch(rawText, source) {
    const text = String(rawText || '').trim();
    if (!text || searching) return;
    setVoiceState('idle');
    setSearching(true);
    try {
      const r = await fetch('/api/search/parse', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, source }),
      });
      const d = r.ok ? await r.json() : null;
      const res = d && d.status === 'ok' ? d.resolution : null;
      if (res && (res.action === 'checkout' || res.action === 'page') && res.url) {
        track('search', { query: text, source, market: market.key, action: res.action, reason: res.reason });
        window.location.href = res.url;
        return;
      }
      if (res && res.action === 'none' && source === 'voice') setVoiceState('nomatch');
    } catch (err) { /* keep the tile filter */ }
    setSearching(false);
  }

  const voiceHint = voiceState === 'nomatch'
    ? `Didn’t catch a product in “${filter}”. Try ${market.voiceExample}.`
    : (VOICE_HINTS[voiceState] || null);

  function pickKind(k) {
    setKind(k); setFilter('');
    const el = k === 'all' ? null : document.getElementById(`mk-${k}`);
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 70, behavior: 'smooth' });
  }

  const loading = !data;

  return (
    <div className="page mk">
      <Header me={me} active="market" currency={currency} onCurrency={onCurrency} homeHref={`/${market.key}`} />

      <div className="hero" style={heroStyle(heroUrl)}>
        <p className="mk-where"><span aria-hidden="true">{market.flag}</span> Shopping from {market.place}. Prices in {market.symbol}</p>
        <form className="finder" onSubmit={(e) => { e.preventDefault(); runSearch(filter, 'text'); }}>
          <div className="search-wrap">
            <div className="search">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="6" cy="6" r="4.6" stroke="currentColor" strokeWidth="1.6" /><path d="M9.5 9.5 13 13" stroke="currentColor" strokeWidth="1.6" /></svg>
              <input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Search brands, networks or countries"
                aria-label="Search brands, networks or countries"
                aria-busy={searching}
                disabled={searching}
              />
              <VoiceButton
                disabled={searching}
                onState={setVoiceState}
                onInterim={(text) => setFilter(text)}
                onTranscript={(text) => { setFilter(text); runSearch(text, 'voice'); }}
              />
            </div>
            {voiceHint && <div className={`voice-hint ${voiceState}`} role="status">{voiceHint}</div>}
          </div>
        </form>
      </div>

      <div className="mk-bar"><div className="mk-chips" role="group" aria-label="Filter products">
        {CHIPS.map(([k, label]) => (
          <button key={k} type="button" className="mk-chip" aria-pressed={!searchingLocal && kind === k} onClick={() => pickKind(k)}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{ICON[k]}</svg>
            {label}
          </button>
        ))}
      </div></div>

      <main className="mk-main">
        {!searchingLocal && kind === 'all' && tiles.popular.length > 0 && (
          <Row id="mk-popular" title="Most popular">
            <div className="mk-grid">{tiles.popular.map((t) => <Tile key={t.id} t={t} badge />)}</div>
          </Row>
        )}

        <Row id="mk-topup" title="Mobile top-up" hidden={!show('topup')} more={{ href: `/topup/${market.slug}`, label: 'Show all ›' }}>
          <div className="mk-grid">{rows.topup.filter((t) => tiles.major.includes(t)).map((t) => <Tile key={t.id} t={t} />)}</div>
          {rows.topup.some((t) => tiles.minor.includes(t)) && (
            <>
              <h3 className="mk-sub-head">More {market.name} networks</h3>
              <div className="mk-mini">{rows.topup.filter((t) => tiles.minor.includes(t)).map((t) => <Tile key={t.id} t={t} />)}</div>
            </>
          )}
        </Row>

        {!searchingLocal && kind === 'all' && (
          <section className="mk-band">
            <h1>{market.headline}</h1>
            <ul>
              <li>Delivered in seconds</li>
              <li>Support 24/7</li>
              <li>1M+ orders since 2009</li>
            </ul>
          </section>
        )}

        <Row id="mk-gift" title="Gift cards" hidden={!show('gift')} more={{ href: `/gifts/${market.slug}`, label: 'Show all ›' }}>
          <div className="mk-grid">{rows.gift.map((t) => <Tile key={t.id} t={t} />)}</div>
        </Row>
        <Row id="mk-game" title="Game vouchers" hidden={!show('game')} more={{ href: `/gifts/${market.slug}`, label: 'Show all ›' }}>
          <div className="mk-grid">{rows.game.map((t) => <Tile key={t.id} t={t} />)}</div>
        </Row>
        <Row id="mk-esim" title="eSIM data for your trip" hidden={!show('esim')} more={{ href: '/dataplans', label: 'All destinations ›' }}>
          <div className="mk-grid">{rows.esim.map((t) => <Tile key={t.id} t={t} />)}</div>
        </Row>

        {loading && <p className="mk-note">Loading products…</p>}
        {!loading && searchingLocal && !anyMatch && (
          <div className="mk-empty">
            <p>No match for “{filter}”.</p>
            <p><a href="#" onClick={(e) => { e.preventDefault(); runSearch(filter, 'text'); }}>Search everything</a> or <a href="/support">ask us to add it</a>.</p>
          </div>
        )}

        {!searchingLocal && (
          <>
            <PayBox />
            <ul className="mk-notes">
              <li>Prices are shown in {market.symbol}.</li>
              <li>You are charged in USD or THB at checkout.</li>
            </ul>
          </>
        )}
      </main>

      <SiteFooterV2 currency={currency} onCurrency={onCurrency} />
    </div>
  );
}
